import {
  Box,
  CircularProgress,
  Grid,
  IconButton,
  Paper,
  Tab,
  Tabs,
  Typography,
} from '@material-ui/core'
import { getFormattedDate } from 'components/domain/backoffice/BackofficePresenter'
import { Button, CloseIcon, CustomTooltip, LanguageIcon } from 'components/facets'
//@ts-ignore
import NewWindow from 'components/facets/NewWindow.tsx'
import NumberField from 'components/facets/NumberField'
import { PriceSuggestArticle } from 'components/facets/PriceSuggestArticle'
import { usePriceGuide } from 'components/providers/PriceGuideProvider'
import { useUser } from 'components/providers/UserProvider'
import {
  fetchProduct,
  getCheapestMatches,
  getPurchasesLinkedToPtProduct,
  updateTaxationScheme,
} from 'lib/api'
import { useLocalStorageState, useScopelessHotkeys } from 'lib/hooks'
import { ApiCallStatus, useApiCall } from 'lib/hooks/useApiCall'
import { useStockStore } from 'lib/hooks/useStockStore'
import { currencySymbol, getCurrencySymbol, getPlaysetIcon } from 'lib/utils'
import _ from 'lodash'
import { useSnackbar } from 'notistack'
import { useEffect, useState } from 'react'
import Accordion from 'react-bootstrap/Accordion'
import { useTranslation } from 'react-i18next'
import {
  CardmarketArticle,
  InventoryArticle,
  normalizePurchase,
  PriceGuideMetrics,
  Purchase,
  toSlimInventoryArticle,
} from 'shared'
import {
  BACKOFFICE_USER_STOCK,
  ESCAPE_STRING_FOR_EMPTY_COMMENT,
  LISTING_AND_APPRAISAL,
  MARGIN_TAXATION_ICON,
  PRICE_SUGGEST_PRESENTER,
  STANDARD_TAXATION_ICON,
  STOCK_DISPLAY,
} from 'utils/constants'
import { DockAction } from '../InventoryPresenter'
import { BooleanIconButton } from '../PricingSettings/BooleanIconButton'
import { CompetitorBox } from '../PricingSettings/CompetitorBox'
import { EditableElement } from '../PricingSettings/EditableElement'
import { VisualArticle } from '../StockDisplay/VisualArticle'
import './price-suggest-presenter.css'

export interface PriceSuggestPresenterProps {
  article: InventoryArticle
  onClose: () => void
  priceArticle: (price: number) => void
  view?: 'buyPrice' | 'sellPrice'
  parentPage: string
  totalQuantity?: number
  refreshStockAction: DockAction | undefined
}

export const PriceSuggestPresenter = ({
  article,
  onClose,
  priceArticle,
  view,
  parentPage,
  // totalQuantity,
  refreshStockAction,
}: PriceSuggestPresenterProps): JSX.Element => {
  const { t } = useTranslation()
  const { enqueueSnackbar } = useSnackbar()

  const { user, activeGame } = useUser()
  const [activeView, setActiveView] = useState<'buyPrice' | 'sellPrice'>(view ?? 'sellPrice')
  const [linkedPurchaseEvents, setLinkedPurchaseEvents] = useState<Purchase[]>([])

  const [workingArticle, setWorkingArticle] = useState(_.cloneDeep(article))
  const [standardQty, setStandardQty] = useState(article.standardTaxationQuantity)
  const [marginQty, setMarginQty] = useState(article.marginTaxationQuantity)

  const [cardmarketPopupShown, setShowCardmarketPopup] = useState(false)
  const [priceSuggestSettings, setPriceSuggestSettings] = useLocalStorageState(
    { competitorId: user?.competitors[0]._id, strictLanguage: false, disablePlaysetPricing: true },
    'priceSuggestSettings'
  )

  const [newPrice, setNewPrice] = useState('')

  const { competitorId, strictLanguage, disablePlaysetPricing } = priceSuggestSettings

  const isReadOnly = parentPage === BACKOFFICE_USER_STOCK

  const [activeKey, setActiveKey] = useState<string | string[] | null | undefined>(undefined)

  const handleAccordionSelect = (eventKey: string | string[] | null | undefined) => {
    // allow opening/closing only if you are in STOCK_DISPLAY
    if (parentPage !== STOCK_DISPLAY) return

    setActiveKey((prev) => (prev === eventKey ? null : eventKey))
  }

  const competitor =
    user?.competitors.find(({ _id }) => _id === competitorId) || user?.competitors[0]

  const priceDataCall = useApiCall<typeof getCheapestMatches>(getCheapestMatches, [
    workingArticle,
    competitor._id,
    { strictLanguage, disablePlaysetPricing },
    activeGame.idGame,
  ])

  // margin + standard must equal totalQuantity - ptArticleReservedQuantity,
  // so each +/- is an atomic conversion between the two
  const increaseMargin = () => {
    if (standardQty > 0) {
      setMarginQty(marginQty + 1)
      setStandardQty(standardQty - 1)
    }
  }

  const decreaseMargin = () => {
    if (marginQty > 0) {
      setMarginQty(marginQty - 1)
      setStandardQty(standardQty + 1)
    }
  }

  const increaseStandard = () => {
    if (marginQty > 0) {
      setStandardQty(standardQty + 1)
      setMarginQty(marginQty - 1)
    }
  }

  const decreaseStandard = () => {
    if (standardQty > 0) {
      setStandardQty(standardQty - 1)
      setMarginQty(marginQty + 1)
    }
  }

  const handleUpdateTaxationScheme = async () => {
    try {
      const newArticle = workingArticle.copy()
      newArticle.newStandardTaxationQuantity = standardQty
      newArticle.newMarginTaxationQuantity = marginQty
      const response = await updateTaxationScheme(toSlimInventoryArticle(newArticle))

      if (response) {
        enqueueSnackbar(t('stockPricing.errors.articleUpdatedSuccessfully'), { variant: 'success' })
        refreshStockAction && refreshStockAction.fn()
        setWorkingArticle(
          new InventoryArticle({
            ...newArticle,
            standardTaxationQuantity: standardQty,
            marginTaxationQuantity: marginQty,
          })
        )
      }
    } catch (error) {
      enqueueSnackbar(t('stockPricing.errors.errorUpdatingArticle'), { variant: 'error' })
    }
  }

  const articlePriceData = priceDataCall.data

  const { getPriceGuide } = usePriceGuide()

  const priceGuide = getPriceGuide(article.card._id)

  const fetchProductCall = useApiCall<typeof fetchProduct>(fetchProduct, [article.card._id])

  useEffect(() => {
    fetchProductCall.performCall(article.card._id)

    if (activeView === 'buyPrice') {
      void (async () => {
        const data = await getPurchasesLinkedToPtProduct(article.ptProductId!)
        const normalized = data.map(normalizePurchase)
        setLinkedPurchaseEvents(normalized)
      })()
    }
  }, [article, activeView])

  const showCardmarketPopup = (): void => {
    setShowCardmarketPopup(false)
    setTimeout(() => setShowCardmarketPopup(true), 200)
  }

  const activeStockType = useStockStore((state) => state.activeStockType)

  useEffect(() => {
    priceDataCall.performCall(
      article,
      competitor._id,
      { strictLanguage, disablePlaysetPricing },
      activeGame.idGame
    )
  }, [competitor?._id, strictLanguage, disablePlaysetPricing])

  useScopelessHotkeys('v', showCardmarketPopup)
  useScopelessHotkeys('enter', () => priceArticle(Number(newPrice)), [newPrice])

  const articleComment = () => {
    if (
      article.newComment === ESCAPE_STRING_FOR_EMPTY_COMMENT ||
      article.comment === ESCAPE_STRING_FOR_EMPTY_COMMENT
    )
      return undefined
    return article.newComment ?? article.comment
  }

  const handleChange = (_event: React.ChangeEvent<Record<string, unknown>>, newValue: any) => {
    setActiveView(newValue)
  }

  const getContentAccordingActiveView = (view: 'buyPrice' | 'sellPrice') => {
    if (view === 'sellPrice') {
      return (
        <>
          <div>
            <h5 className="fw-semibold px-3 pt-2">
              {article.card.name} {article.card.set}
            </h5>
            <div className="mx-auto">
              <Grid container>
                <Grid item xs={6}>
                  <div className="mb-3">
                    <VisualArticle
                      visualArticleSizeClass={'three'}
                      rowHeight={250}
                      article={workingArticle}
                      currency={user.currency}
                      displayOnly
                      parentEl={PRICE_SUGGEST_PRESENTER}
                    />
                  </div>
                </Grid>
                <Grid item xs={6}>
                  <div className="p-3">
                    <div className="d-flex flex-column">
                      <div className="d-flex flex-row align-items-center">
                        <img alt="standardTaxation" src={STANDARD_TAXATION_ICON} />
                        <h5 className="m-0 p-0">
                          {workingArticle.standardTaxationQuantity} {t('taxation.forSaleWithVat')}
                        </h5>
                      </div>
                      <div className="d-flex flex-row align-items-center">
                        <img alt="marginTaxation" src={MARGIN_TAXATION_ICON} />
                        <h5 className="m-0 p-0">
                          {workingArticle.marginTaxationQuantity} {t('taxation.forSaleWithMargin')}
                        </h5>
                      </div>
                    </div>
                  </div>
                </Grid>
              </Grid>
            </div>
            <div className="mb-5">
              <Accordion
                activeKey={activeKey ?? undefined}
                onSelect={handleAccordionSelect}
                alwaysOpen={false}
                data-testid="update-taxation-accordion"
              >
                <Accordion.Item eventKey="0">
                  <Accordion.Header>{t('taxation.updateTaxationQuantities')}</Accordion.Header>
                  <Accordion.Body>
                    <div className="mt-2">
                      <h6 className="m-0 p-0">
                        {t('taxation.updateTaxationQuantitiesDescription')}
                      </h6>

                      <div className="d-flex flex-row align-items-center">
                        <p className="m-0 p-0">{t('stockPricing.standardIcon')}</p>
                        <IconButton
                          onClick={decreaseStandard}
                          data-testid="decrease-standard"
                          disabled={isReadOnly || !user.enableMarginTaxation}
                        >
                          <i className="fa-light fa-circle-minus interactions-button"></i>
                        </IconButton>
                        <span data-testid="updated-standard-qty">{standardQty}</span>
                        <IconButton
                          onClick={increaseStandard}
                          data-testid="increase-standard"
                          disabled={isReadOnly || !user.enableMarginTaxation}
                        >
                          <i className="fa-light fa-circle-plus interactions-button"></i>
                        </IconButton>
                      </div>

                      <div className="d-flex flex-row  align-items-center mt-2">
                        <p className="m-0 p-0">{t('stockPricing.marginIcon')}</p>
                        <IconButton
                          onClick={decreaseMargin}
                          data-testid="decrease-margin"
                          disabled={isReadOnly || !user.enableMarginTaxation}
                        >
                          <i className="fa-light fa-circle-minus interactions-button"></i>
                        </IconButton>
                        <span data-testid="updated-margin-qty">{marginQty}</span>{' '}
                        <IconButton
                          onClick={increaseMargin}
                          data-testid="increase-margin"
                          disabled={isReadOnly || !user.enableMarginTaxation}
                        >
                          <i className="fa-light fa-circle-plus interactions-button"></i>
                        </IconButton>
                      </div>

                      {!isReadOnly && (
                        <Button
                          variant="contained"
                          data-testid="save-new-tax-scheme"
                          onClick={handleUpdateTaxationScheme}
                          disabled={
                            parentPage === LISTING_AND_APPRAISAL || !user.enableMarginTaxation
                          }
                        >
                          {t('common.save')}
                        </Button>
                      )}
                    </div>
                  </Accordion.Body>
                </Accordion.Item>
              </Accordion>
            </div>
          </div>
          <div>
            <h6>{t('competitor.title')}</h6>
            <div className="p-3">
              {article.comment && (
                <div className="d-flex">
                  <i
                    className={`fa-light fa-message-dots comment-icon me-1 ${
                      articleComment() === undefined || articleComment() === ''
                        ? 'missing-comment'
                        : ''
                    }`}
                  ></i>
                  <Typography variant="body1" style={{ paddingLeft: '4px' }}>
                    {articleComment()}
                  </Typography>
                </div>
              )}
              {activeStockType === 'singles' && (
                <div className="mt-2" style={{ width: '130px' }}>
                  {Object.keys(PriceGuideMetrics).map((metric) => (
                    <div key={metric} className="d-flex justify-content-between">
                      <Typography variant="body1">
                        <Typography component="span" color="textSecondary">
                          {(article.isShiny ? 'Foil' : '') + metric}{' '}
                        </Typography>{' '}
                      </Typography>
                      <Typography variant="body1">
                        <b>
                          {articlePriceData
                            ? priceGuide[
                                ((article.isShiny ? 'Foil' : '') +
                                  metric) as keyof typeof PriceGuideMetrics
                              ]
                            : '...'}
                          {currencySymbol(user?.currency)}
                        </b>
                      </Typography>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="d-flex align-items-center justify-content-center">
              <div className="competitor-box-container mt-1">
                <div className="d-flex align-items-center justify-content-center mb-2">
                  <h6 className="m-0 p-0">
                    <EditableElement
                      title
                      value={competitor!.name}
                      tooltipText="change"
                      attribute="name"
                      type="select"
                      onChange={(newValue) =>
                        setPriceSuggestSettings({
                          ...priceSuggestSettings,
                          competitorId: newValue as string,
                        })
                      }
                      existingValue={competitor!.name}
                      name={t('competitor.title')}
                      dataTestid={`competitor-${competitor!.name}`}
                      options={
                        user?.competitors?.map((competitor) => ({
                          label: competitor.name,
                          value: competitor._id,
                        })) || []
                      }
                    />
                  </h6>
                  <div>
                    <CustomTooltip title={<CompetitorBox competitor={competitor!} />}>
                      <i className="fa-light fa-circle-question price-suggest-presenter-tooltip-icons"></i>
                    </CustomTooltip>

                    {!!activeGame.playsetCount && disablePlaysetPricing === false && (
                      <EditableElement
                        existingValue={disablePlaysetPricing}
                        attribute="disablePlaysetPricing"
                        onChange={(newValue) =>
                          setPriceSuggestSettings({
                            ...priceSuggestSettings,
                            disablePlaysetPricing: newValue as boolean,
                          })
                        }
                        sameTooltipForViewOnly
                        type="bool"
                        value={
                          <BooleanIconButton
                            active={!disablePlaysetPricing}
                            Icon={getPlaysetIcon(activeGame.playsetCount)}
                            icon={`fa-sharp fa-light fa-circle-4 mx-1 price-suggest-presenter-tooltip-icons ${
                              !disablePlaysetPricing ? 'active' : ''
                            }`}
                          />
                        }
                        tooltipText={
                          <>
                            {t(
                              `strategy.tooltip.playsetPricing.${
                                disablePlaysetPricing ? 'inactive' : 'active'
                              }`,
                              { playsetCount: activeGame.playsetCount }
                            )}
                            <span
                              className="d-block mt-1 px-2 py-1 rounded"
                              style={{ backgroundColor: 'var(--warning-color)' }}
                            >
                              <i className="fa-solid fa-triangle-exclamation me-1" />
                              {t('strategy.tooltip.playsetPricingOffWarning')}
                            </span>
                          </>
                        }
                        name={t('strategy.playsetPricing')}
                      />
                    )}
                    <EditableElement
                      existingValue={priceSuggestSettings.strictLanguage}
                      attribute="strictLanguage"
                      type="bool"
                      sameTooltipForViewOnly
                      onChange={(newValue) =>
                        setPriceSuggestSettings({
                          ...priceSuggestSettings,
                          strictLanguage: newValue as boolean,
                        })
                      }
                      value={
                        <BooleanIconButton
                          icon={`fa-light fa-globe price-suggest-presenter-tooltip-icons ${
                            Boolean(priceSuggestSettings.strictLanguage) ? 'active' : ''
                          } mx-1`}
                          active={Boolean(priceSuggestSettings.strictLanguage)}
                          Icon={LanguageIcon}
                        />
                      }
                      tooltipText={t(
                        `strategy.tooltip.strictLanguage.${
                          priceSuggestSettings.strictLanguage ? 'active' : 'inactive'
                        }`
                      )}
                      name={t('strategy.strictLanguage')}
                    />
                  </div>
                </div>
                <div className="d-flex align-items-center justify-content-center mb-5">
                  <div>
                    {ApiCallStatus.Success === priceDataCall.status && (
                      <div>
                        {articlePriceData?.cheapestMatches.map((article, index: number) => (
                          <div className="p-1" key={index}>
                            <Paper className="competitors-box">
                              <div className="p-2 px-3">
                                <PriceSuggestArticle article={new CardmarketArticle(article)} />
                              </div>
                            </Paper>
                          </div>
                        ))}
                        {!articlePriceData!.cheapestMatches.length && (
                          <p className="text-center">
                            {t('stockPricing.priceSuggest.noMatchesFound')}
                          </p>
                        )}
                      </div>
                    )}
                    {ApiCallStatus.Fetching === priceDataCall.status && (
                      <Box width="fit-content" margin="0 auto">
                        <CircularProgress color="primary" size={100} />
                      </Box>
                    )}
                    <div className="mb-5 mt-2">
                      <Button onClick={showCardmarketPopup} color="primary" fullWidth hotkey="V">
                        {t('suggest.viewOnCardmarket')}
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          {!isReadOnly && (
            <div className="p-4 d-flex mx-1 justify-content-between">
              <div className="w-100">
                <NumberField
                  variant="filled"
                  data-testid="price-suggest-input-price"
                  autoFocus
                  name="price"
                  fullWidth
                  label={t('card.price')}
                  value={newPrice}
                  onChange={(e) => setNewPrice(e.target.value || '')}
                />
              </div>
              <Button
                data-testid="price-suggest-save"
                disabled={!Number(newPrice)}
                variant="contained"
                onClick={() => priceArticle(Number(newPrice))}
              >
                {t('common.save')}
              </Button>
            </div>
          )}
        </>
      )
    } else {
      return (
        <>
          <div>
            <h5 className="fw-semibold px-3 pt-2">
              {article.card.name} {article.card.set}
            </h5>
            <div className="mx-auto">
              <Grid container spacing={3}>
                <Grid item xs={6}>
                  <div className="mb-3">
                    <VisualArticle
                      visualArticleSizeClass={'three'}
                      rowHeight={250}
                      article={article}
                      currency={user.currency}
                      displayOnly
                      parentEl={PRICE_SUGGEST_PRESENTER}
                    />
                  </div>
                </Grid>
              </Grid>

              <div className="mt-3">
                <p>This product has been linked to the following lots: </p>

                <div>
                  <table className="table">
                    <tbody>
                      {linkedPurchaseEvents.length > 0 ? (
                        linkedPurchaseEvents.map((event) => (
                          <tr key={event.id}>
                            <td>
                              <div className="d-flex flex-column">
                                <span>{event.purchaseEvent?.seller?.name}</span>
                                <span>{event.purchaseEvent!.lotNumber}</span>
                                <span>{getFormattedDate(event.purchaseEvent!.purchaseDate)}</span>
                              </div>
                            </td>
                            <td>{event.quantity} items</td>
                            <td>
                              {event.price.toFixed(2)}{' '}
                              {getCurrencySymbol(event.purchaseEvent!.currency)}
                            </td>
                            <td>{event.isVat ? 'VAT' : 'no VAT'}</td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan={4}>
                            <div className="text-center text-muted">
                              No purchase events available
                            </div>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </>
      )
    }
  }

  return (
    <>
      {!user.featureFlags?.taxation || !user.enableMarginTaxation ? (
        <div className="price-suggest-presenter-container">
          <div className="px-3 py-1">
            <div className="d-flex justify-content-start">
              <IconButton onClick={onClose}>
                <CloseIcon />
              </IconButton>
            </div>
            <div>
              <h5 className="fw-semibold px-3 pt-2">
                {article.card.name} {article.card.set}
              </h5>
              <div className="mx-auto">
                <Grid container spacing={3}>
                  <Grid item xs={6}>
                    <div className="mb-3">
                      <VisualArticle
                        visualArticleSizeClass={'three'}
                        rowHeight={250}
                        article={article}
                        currency={user.currency}
                        displayOnly
                        parentEl={PRICE_SUGGEST_PRESENTER}
                      />
                    </div>
                  </Grid>
                  <Grid item xs={4}>
                    <div className="p-3">
                      {article.comment && (
                        <div className="d-flex">
                          <i
                            className={`fa-light fa-message-dots comment-icon me-1 ${
                              articleComment() === undefined || articleComment() === ''
                                ? 'missing-comment'
                                : ''
                            }`}
                          ></i>
                          <Typography variant="body1" style={{ paddingLeft: '4px' }}>
                            {articleComment()}
                          </Typography>
                        </div>
                      )}
                      {activeStockType === 'singles' && (
                        <div className="mt-2" style={{ width: '130px' }}>
                          {Object.keys(PriceGuideMetrics).map((metric) => (
                            <div key={metric} className="d-flex justify-content-between">
                              <Typography variant="body1">
                                <Typography component="span" color="textSecondary">
                                  {(article.isShiny ? 'Foil' : '') + metric}{' '}
                                </Typography>{' '}
                              </Typography>
                              <Typography variant="body1">
                                <b>
                                  {articlePriceData
                                    ? priceGuide[
                                        ((article.isShiny ? 'Foil' : '') +
                                          metric) as keyof typeof PriceGuideMetrics
                                      ]
                                    : '...'}
                                  {currencySymbol(user?.currency)}
                                </b>
                              </Typography>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </Grid>
                </Grid>
              </div>
            </div>
            <div>
              <div className="d-flex align-items-center justify-content-center">
                <div className="competitor-box-container mt-1">
                  <div className="d-flex align-items-center justify-content-center mb-2">
                    <h6 className="m-0 p-0">
                      <EditableElement
                        title
                        value={competitor!.name}
                        tooltipText="change"
                        attribute="name"
                        type="select"
                        onChange={(newValue) =>
                          setPriceSuggestSettings({
                            ...priceSuggestSettings,
                            competitorId: newValue as string,
                          })
                        }
                        existingValue={competitor!.name}
                        name={t('competitor.title')}
                        dataTestid={`competitor-${competitor!.name}`}
                        options={
                          user?.competitors?.map((competitor) => ({
                            label: competitor.name,
                            value: competitor._id,
                          })) || []
                        }
                      />
                    </h6>
                    <div>
                      <CustomTooltip title={<CompetitorBox competitor={competitor!} />}>
                        <i className="fa-light fa-circle-question price-suggest-presenter-tooltip-icons"></i>
                      </CustomTooltip>

                      {!!activeGame.playsetCount && disablePlaysetPricing === false && (
                        <EditableElement
                          existingValue={disablePlaysetPricing}
                          attribute="disablePlaysetPricing"
                          onChange={(newValue) =>
                            setPriceSuggestSettings({
                              ...priceSuggestSettings,
                              disablePlaysetPricing: newValue as boolean,
                            })
                          }
                          sameTooltipForViewOnly
                          type="bool"
                          value={
                            <BooleanIconButton
                              active={!disablePlaysetPricing}
                              Icon={getPlaysetIcon(activeGame.playsetCount)}
                              icon={`fa-sharp fa-light fa-circle-4 mx-1 price-suggest-presenter-tooltip-icons ${
                                !disablePlaysetPricing ? 'active' : ''
                              }`}
                            />
                          }
                          tooltipText={
                            <>
                              {t(
                                `strategy.tooltip.playsetPricing.${
                                  disablePlaysetPricing ? 'inactive' : 'active'
                                }`,
                                { playsetCount: activeGame.playsetCount }
                              )}
                              <span
                                className="d-block mt-1 px-2 py-1 rounded"
                                style={{ backgroundColor: 'var(--warning-color)' }}
                              >
                                <i className="fa-solid fa-triangle-exclamation me-1" />
                                {t('strategy.tooltip.playsetPricingOffWarning')}
                              </span>
                            </>
                          }
                          name={t('strategy.playsetPricing')}
                        />
                      )}
                      <EditableElement
                        existingValue={priceSuggestSettings.strictLanguage}
                        attribute="strictLanguage"
                        type="bool"
                        sameTooltipForViewOnly
                        onChange={(newValue) =>
                          setPriceSuggestSettings({
                            ...priceSuggestSettings,
                            strictLanguage: newValue as boolean,
                          })
                        }
                        value={
                          <BooleanIconButton
                            icon={`fa-light fa-globe price-suggest-presenter-tooltip-icons ${
                              Boolean(priceSuggestSettings.strictLanguage) ? 'active' : ''
                            } mx-1`}
                            active={Boolean(priceSuggestSettings.strictLanguage)}
                            Icon={LanguageIcon}
                          />
                        }
                        tooltipText={t(
                          `strategy.tooltip.strictLanguage.${
                            priceSuggestSettings.strictLanguage ? 'active' : 'inactive'
                          }`
                        )}
                        name={t('strategy.strictLanguage')}
                      />
                    </div>
                  </div>
                  <div className="d-flex align-items-center justify-content-center mb-5">
                    <div>
                      {ApiCallStatus.Success === priceDataCall.status && (
                        <div>
                          {articlePriceData?.cheapestMatches.map((article, index: number) => (
                            <div className="p-1" key={index}>
                              <Paper className="competitors-box">
                                <div className="p-2 px-3">
                                  <PriceSuggestArticle article={new CardmarketArticle(article)} />
                                </div>
                              </Paper>
                            </div>
                          ))}
                          {!articlePriceData!.cheapestMatches.length && (
                            <p className="text-center">
                              {t('stockPricing.priceSuggest.noMatchesFound')}
                            </p>
                          )}
                        </div>
                      )}
                      {ApiCallStatus.Fetching === priceDataCall.status && (
                        <Box width="fit-content" margin="0 auto">
                          <CircularProgress color="primary" size={100} />
                        </Box>
                      )}
                      <div className="mb-5 mt-2">
                        <Button onClick={showCardmarketPopup} color="primary" fullWidth hotkey="V">
                          {t('suggest.viewOnCardmarket')}
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          {!isReadOnly && (
            <div className="p-4 d-flex mx-1 justify-content-between">
              <div className="w-100">
                <NumberField
                  variant="filled"
                  data-testid="price-suggest-input-price"
                  autoFocus
                  name="price"
                  fullWidth
                  label={t('card.price')}
                  value={newPrice}
                  onChange={(e) => setNewPrice(e.target.value || '')}
                />
              </div>
              <Button
                data-testid="price-suggest-save"
                disabled={!Number(newPrice)}
                variant="contained"
                onClick={() => priceArticle(Number(newPrice))}
              >
                {t('common.save')}
              </Button>
            </div>
          )}
          {cardmarketPopupShown ? (
            <NewWindow
              url={(function () {
                const url =
                  `https://www.cardmarket.com/en/${
                    activeGame.cardmarketName || activeGame.name
                  }/Products?idProduct=${article.idProduct}` || ''

                return url
              })()}
            />
          ) : null}
        </div>
      ) : (
        <div className="price-suggest-presenter-container">
          <div className="filter-dock-action-buttons-external-container">
            <Tabs
              value={activeView}
              textColor="primary"
              indicatorColor="primary"
              onChange={handleChange}
              // variant="fullWidth"
              centered
            >
              <Tab label={t('suggest.sell')} value="sellPrice" />
              <Tab label={t('suggest.supplies')} value="buyPrice" />
            </Tabs>
          </div>

          <div className="px-3 py-1">{getContentAccordingActiveView(activeView)}</div>
          {cardmarketPopupShown ? (
            <NewWindow
              url={(function () {
                const url =
                  `https://www.cardmarket.com/en/${
                    activeGame.cardmarketName || activeGame.name
                  }/Products?idProduct=${article.idProduct}` || ''

                console.log('rp', url)
                return url
              })()}
            />
          ) : null}
        </div>
      )}
    </>
  )
}
